// components/sessions/SessionsTab.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip, Avatar,
  Grid, TextField, InputAdornment, MenuItem, FormControlLabel, Switch,
  Alert, CircularProgress, Tooltip, Badge, Stack, Divider,
  List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  Pagination, Paper, Skeleton, Menu
} from '@mui/material';
import {
  Search as SearchIcon, FilterList as FilterListIcon, Delete as DeleteIcon,
  Archive as ArchiveIcon, Launch as LaunchIcon, Refresh as RefreshIcon,
  Download as DownloadIcon, MoreVert as MoreVertIcon, Close as CloseIcon,
  Chat as ChatIcon, Group as GroupIcon, AccessTime as AccessTimeIcon,
  Person as PersonIcon, SmartToy as SmartToyIcon, Message as MessageIcon,
  FolderOpen as FolderOpenIcon, DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { BACKEND_URL } from '../../config';  // due livelli sopra per raggiungere /src/config.js
import MessageTimeline from './MessageTimeline';  // stesso livello (entrambi in /components/sessions/)
import SessionFilters from './SessionFilters';    // stesso livello (entrambi in /components/sessions/)

const SessionsTab = ({
  user,
  profile,
  themeColors,
  domainConfig,
  projectId,
  sessionType = 'chat',
  tabConfig = {}
}) => {
  // State management
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    includeEmpty: false,
    agentFilter: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  
  // Dialog tabs
  const [dialogTab, setDialogTab] = useState(0);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    if (!user || !projectId) return;
    
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({
        project_id: projectId,
        session_type: sessionType,
        order_by: 'updated_at',
        order: 'desc',
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        include_empty: filters.includeEmpty
      });
      
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/sessions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions);
        setTotalCount(data.total);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [user, projectId, sessionType, page, rowsPerPage, filters]);

  // Fetch session messages
  const fetchMessages = async (sessionId) => {
    if (!user) return;
    
    setLoadingMessages(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/api/sessions/${sessionId}/messages`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    if (!user || !projectId) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/api/sessions/stats?project_id=${projectId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [user, projectId]);

  useEffect(() => {
    fetchSessions();
    fetchStats();
  }, [fetchSessions, fetchStats]);

  // Handlers
  const handleOpenDialog = (session) => {
    setSelectedSession(session);
    setDialogOpen(true);
    setDialogTab(0);
    fetchMessages(session.id);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSession(null);
    setMessages([]);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!user) return;
    
    if (!window.confirm('Sei sicuro di voler eliminare questa sessione?')) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/api/sessions/${sessionId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        fetchSessions();
        if (selectedSession?.id === sessionId) {
          handleCloseDialog();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (!user || selectedIds.length === 0) return;
    
    if (!window.confirm(`Eliminare ${selectedIds.length} sessioni?`)) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/sessions/bulk`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedIds)
      });
      
      if (response.ok) {
        setSelectedIds([]);
        fetchSessions();
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
    }
  };

  const handleArchiveSession = async (sessionId) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/api/sessions/${sessionId}/archive`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        fetchSessions();
      }
    } catch (error) {
      console.error('Error archiving session:', error);
    }
  };

  const handleContinueChat = (sessionId) => {
    // Naviga alla chat con questa sessione
    window.location.href = `/chat?session=${sessionId}`;
  };

  const toggleSelectSession = (sessionId) => {
    setSelectedIds(prev => 
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === sessions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sessions.map(s => s.id));
    }
  };

  // Filtered sessions based on search
  const filteredSessions = sessions.filter(session => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      session.meeting_title?.toLowerCase().includes(query) ||
      session.session_name?.toLowerCase().includes(query) ||
      session.last_message_preview?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ora';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString('it-IT');
  };

  const getSessionIcon = () => {
    return sessionType === 'meeting' ? <GroupIcon /> : <ChatIcon />;
  };

  const getEmptyMessage = () => {
    if (sessionType === 'meeting') {
      return 'Nessun meeting trovato';
    }
    return 'Nessuna chat trovata';
  };

  // Render
  if (loading && sessions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={400} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con statistiche */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: `${themeColors.primary}10` }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Totale {sessionType === 'meeting' ? 'Meeting' : 'Chat'}
                </Typography>
                <Typography variant="h4" sx={{ color: themeColors.primary }}>
                  {sessionType === 'meeting' ? stats.total_meetings : stats.total_chats}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Con Messaggi
                </Typography>
                <Typography variant="h4">
                  {stats.sessions_with_messages}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Totale Messaggi
                </Typography>
                <Typography variant="h4">
                  {stats.total_messages}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Agenti Attivi
                </Typography>
                <Typography variant="h4">
                  {stats.top_agents?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder={`Cerca ${sessionType === 'meeting' ? 'meeting' : 'chat'}...`}
            variant="outlined"
            size="small"
            sx={{ flexGrow: 1 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtri
          </Button>
          
          <IconButton onClick={fetchSessions}>
            <RefreshIcon />
          </IconButton>
          
          {selectedIds.length > 0 && (
            <>
              <Chip
                label={`${selectedIds.length} selezionate`}
                onDelete={() => setSelectedIds([])}
              />
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleBulkDelete}
              >
                Elimina
              </Button>
            </>
          )}
        </Stack>
        
        {showFilters && (
          <SessionFilters
            filters={filters}
            setFilters={setFilters}
            sessionType={sessionType}
          />
        )}
      </Paper>

      {/* Lista Sessioni */}
      {filteredSessions.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            {getSessionIcon()}
            <Typography variant="h6" sx={{ mt: 2 }}>
              {getEmptyMessage()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery 
                ? 'Prova a modificare i criteri di ricerca'
                : tabConfig.emptyMessage || 'Non ci sono sessioni da visualizzare'
              }
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {filteredSessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              sessionType={sessionType}
              themeColors={themeColors}
              selected={selectedIds.includes(session.id)}
              onToggleSelect={() => toggleSelectSession(session.id)}
              onOpen={() => handleOpenDialog(session)}
              onDelete={() => handleDeleteSession(session.id)}
              onArchive={() => handleArchiveSession(session.id)}
              onContinue={() => handleContinueChat(session.id)}
              formatDate={formatDate}
            />
          ))}
        </List>
      )}

      {/* Paginazione */}
      {totalCount > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page + 1}
            onChange={(e, newPage) => setPage(newPage - 1)}
            color="primary"
          />
        </Box>
      )}

      {/* Dialog Dettagli */}
      <SessionDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        session={selectedSession}
        messages={messages}
        loadingMessages={loadingMessages}
        themeColors={themeColors}
        sessionType={sessionType}
        dialogTab={dialogTab}
        setDialogTab={setDialogTab}
      />
    </Box>
  );
};

// Session Card Component
const SessionCard = ({
  session,
  sessionType,
  themeColors,
  selected,
  onToggleSelect,
  onOpen,
  onDelete,
  onArchive,
  onContinue,
  formatDate
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const hasMessages = session.total_messages > 0;
  const isArchived = session.meeting_status === 'archived';
  
  return (
    <Card sx={{ mb: 1, opacity: isArchived ? 0.7 : 1 }}>
      <ListItem
        button
        onClick={onOpen}
        sx={{
          '&:hover': { bgcolor: `${themeColors.primary}08` }
        }}
      >
        <ListItemAvatar>
          <Badge
            badgeContent={session.total_messages}
            color="primary"
            invisible={!hasMessages}
          >
            <Avatar sx={{ bgcolor: themeColors.primary }}>
              {sessionType === 'meeting' ? <GroupIcon /> : <ChatIcon />}
            </Avatar>
          </Badge>
        </ListItemAvatar>
        
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {session.meeting_title || 'Sessione senza titolo'}
              </Typography>
              {isArchived && (
                <Chip label="Archiviata" size="small" variant="outlined" />
              )}
              {sessionType === 'meeting' && session.leader_drive_enabled && (
                <Chip 
                  label="Leader Drive" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary" noWrap>
                {session.last_message_preview || 'Nessun messaggio'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {formatDate(session.updated_at)}
                </Typography>
                {session.unique_agents?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    <SmartToyIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {session.unique_agents.length} agenti
                  </Typography>
                )}
                {session.total_messages > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    <MessageIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {session.total_messages} messaggi
                  </Typography>
                )}
              </Box>
            </Box>
          }
        />
        
        <ListItemSecondaryAction>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => { onContinue(); setAnchorEl(null); }}>
              <LaunchIcon sx={{ mr: 1 }} /> Continua
            </MenuItem>
            <MenuItem onClick={() => { onArchive(); setAnchorEl(null); }}>
              <ArchiveIcon sx={{ mr: 1 }} /> Archivia
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { onDelete(); setAnchorEl(null); }}>
              <DeleteIcon sx={{ mr: 1, color: 'error.main' }} /> Elimina
            </MenuItem>
          </Menu>
        </ListItemSecondaryAction>
      </ListItem>
    </Card>
  );
};

// Session Details Dialog
const SessionDetailsDialog = ({
  open,
  onClose,
  session,
  messages,
  loadingMessages,
  themeColors,
  sessionType,
  dialogTab,
  setDialogTab
}) => {
  if (!session) return null;
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {session.meeting_title || 'Dettagli Sessione'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs
          value={dialogTab}
          onChange={(e, val) => setDialogTab(val)}
          sx={{ mt: 1 }}
        >
          <Tab label="Conversazione" />
          <Tab label="Dettagli" />
          {sessionType === 'meeting' && <Tab label="Partecipanti" />}
        </Tabs>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {dialogTab === 0 && (
          <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            {loadingMessages ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <MessageTimeline
                messages={messages}
                themeColors={themeColors}
              />
            )}
          </Box>
        )}
        
        {dialogTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  ID Sessione
                </Typography>
                <Typography variant="body2">
                  {session.id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body2">
                  {session.session_type === 'meeting' ? 'Meeting' : 'Chat'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Creata
                </Typography>
                <Typography variant="body2">
                  {new Date(session.created_at).toLocaleString('it-IT')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Ultimo aggiornamento
                </Typography>
                <Typography variant="body2">
                  {new Date(session.updated_at).toLocaleString('it-IT')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Messaggi totali
                </Typography>
                <Typography variant="body2">
                  {session.total_messages}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Risposte agenti
                </Typography>
                <Typography variant="body2">
                  {session.total_agent_responses}
                </Typography>
              </Grid>
              {sessionType === 'meeting' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Leader Drive
                    </Typography>
                    <Typography variant="body2">
                      {session.leader_drive_enabled ? 'Attivo' : 'Non attivo'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Durata (minuti)
                    </Typography>
                    <Typography variant="body2">
                      {session.meeting_duration_minutes || 0}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}
        
        {dialogTab === 2 && sessionType === 'meeting' && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Partecipanti
            </Typography>
            <List>
              {session.unique_agents?.map((agent, idx) => (
                <ListItem key={idx}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: themeColors.primary }}>
                      <SmartToyIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={agent} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SessionsTab;